import { LegalPageLayout, P, UL, LI, Strong, ContactLink } from './LegalPageLayout';

interface StorageRow {
  name: string;
  kind: 'Cookie' | 'Local storage';
  category: 'essential' | 'preferences' | 'analytics';
  duration: string;
  purpose: string;
}

const STORAGE: StorageRow[] = [
  {
    name: 'plyez_session',
    kind: 'Cookie',
    category: 'essential',
    duration: 'Session / up to 7 days',
    purpose:
      'Carries the authentication token (JWT) that keeps you signed in. Required for the app to function.',
  },
  {
    name: 'i18nextLng',
    kind: 'Local storage',
    category: 'preferences',
    duration: 'Until cleared',
    purpose: 'Remembers your selected interface language across visits.',
  },
  {
    name: 'plyez_theme',
    kind: 'Local storage',
    category: 'preferences',
    duration: 'Until cleared',
    purpose: 'Remembers your light/dark theme preference.',
  },
  {
    name: '__cf_bm',
    kind: 'Cookie',
    category: 'essential',
    duration: '30 minutes',
    purpose:
      'Cloudflare bot management. Distinguishes human visitors from automated traffic.',
  },
  {
    name: 'cf_clearance',
    kind: 'Cookie',
    category: 'essential',
    duration: 'Up to 30 days',
    purpose: 'Records that you passed a Cloudflare security challenge.',
  },
];

const CATEGORY_LABEL: Record<StorageRow['category'], string> = {
  essential: 'Strictly necessary',
  preferences: 'Preferences',
  analytics: 'Analytics',
};

/**
 * Cookie Policy. Inventories the small set of cookies and browser local
 * storage PlyEZ uses (auth/session JWT, language + theme preferences,
 * Cloudflare security). No advertising or cross-site tracking. Cross-links
 * to the Privacy Policy at /legal/privacy.
 */
export function CookiePolicyPage() {
  return (
    <LegalPageLayout
      i18nKey="legal.cookies"
      lastUpdated="2026-06-03"
      lead={
        <>
          PlyEZ uses a deliberately minimal set of cookies and browser storage —
          every item we set has a documented purpose, listed in full below. We do{' '}
          <Strong>not</Strong> use advertising, cross-site tracking, or
          third-party marketing cookies, and we do not load Google Analytics,
          Meta Pixel, or similar trackers on plyez.com or in the application.
        </>
      }
      sections={[
        {
          id: 'what',
          title: '1. Cookies and local storage',
          body: (
            <>
              <P>
                A <Strong>cookie</Strong> is a small text file your browser stores
                when you visit a site; it can hold information that helps the site
                remember you (for example, that you are signed in).{' '}
                <Strong>Local storage</Strong> is a similar browser mechanism that
                keeps small preferences on your device without sending them with
                every request.
              </P>
              <P>
                Some items are set by PlyEZ directly (&ldquo;first-party&rdquo;);
                others are set by infrastructure we rely on, such as Cloudflare for
                security (&ldquo;third-party&rdquo;). This Policy is part of, and
                should be read with, our{' '}
                <ContactLink to="/legal/privacy">Privacy Policy</ContactLink>.
              </P>
            </>
          ),
        },
        {
          id: 'categories',
          title: '2. The categories we use',
          body: (
            <>
              <P>
                <Strong>Strictly necessary</Strong> — required for the Service to
                operate, including authentication and security. We do not ask for
                consent for these because the Service cannot function without them.
              </P>
              <P>
                <Strong>Preferences</Strong> — remember choices such as your
                language and theme. They are optional but improve your experience.
              </P>
              <P>
                <Strong>Analytics</Strong> — we currently set{' '}
                <Strong>no</Strong> analytics or tracking cookies. If we introduce
                privacy-respecting analytics in the future, we will request opt-in
                consent first and update this Policy before doing so.
              </P>
            </>
          ),
        },
        {
          id: 'table',
          title: '3. The full inventory',
          body: (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm border border-border rounded-lg">
                <thead>
                  <tr className="bg-muted/40 text-left">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Type</th>
                    <th className="px-4 py-2 font-medium">Category</th>
                    <th className="px-4 py-2 font-medium">Duration</th>
                    <th className="px-4 py-2 font-medium">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {STORAGE.map((c) => (
                    <tr key={c.name} className="border-t border-border align-top">
                      <td className="px-4 py-2 font-mono text-xs">{c.name}</td>
                      <td className="px-4 py-2 text-foreground/80">{c.kind}</td>
                      <td className="px-4 py-2">{CATEGORY_LABEL[c.category]}</td>
                      <td className="px-4 py-2 num text-xs">{c.duration}</td>
                      <td className="px-4 py-2 text-foreground/80">{c.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
        },
        {
          id: 'consent',
          title: '4. Our consent approach',
          body: (
            <>
              <P>
                Because we set only strictly necessary cookies and a small number of
                preference items — and no advertising or cross-site tracking cookies
                — we do not display an intrusive consent banner. Strictly necessary
                cookies are exempt from prior-consent requirements under frameworks
                such as the EU ePrivacy regime, and preference items are set in
                response to your own actions (choosing a language or theme).
              </P>
              <P>
                Should we ever add analytics or any non-essential, consent-requiring
                technology, we will obtain your opt-in consent first and give you a
                clear way to change your choice.
              </P>
            </>
          ),
        },
        {
          id: 'control',
          title: '5. How to control cookies',
          body: (
            <>
              <P>
                You stay in control of cookies and local storage through your
                browser:
              </P>
              <UL>
                <LI>Most browsers let you view, block, or delete cookies and clear local storage in their privacy or site-data settings.</LI>
                <LI>Blocking the <Strong>plyez_session</Strong> cookie will prevent you from signing in to the Service.</LI>
                <LI>Blocking <Strong>__cf_bm</Strong> or <Strong>cf_clearance</Strong> may cause Cloudflare to challenge or block your requests.</LI>
                <LI>Clearing <Strong>i18nextLng</Strong> or <Strong>plyez_theme</Strong> simply resets your language and theme to defaults.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'changes',
          title: '6. Changes to this Policy',
          body: (
            <P>
              We update this Cookie Policy when our use of cookies or local storage
              changes. The &ldquo;Last updated&rdquo; date above reflects the current
              version, and material changes are announced alongside our{' '}
              <ContactLink to="/legal/privacy">Privacy Policy</ContactLink> updates.
            </P>
          ),
        },
        {
          id: 'contact',
          title: '7. Contact',
          body: (
            <P>
              Questions about cookies? Email{' '}
              <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>{' '}
              or see our{' '}
              <ContactLink to="/legal/privacy">Privacy Policy</ContactLink> for the
              full picture of how we handle personal information.
            </P>
          ),
        },
      ]}
    />
  );
}
